import { clstx } from '@payaca/utilities/cvaMerge';
import React, { ComponentPropsWithoutRef, FC, PropsWithChildren } from 'react';
import FloatingTooltip, {
  IProps as IFloatingTooltipProps,
} from './FloatingTooltip';
import TooltipCore from './TooltipCore';

export type TooltipProps = {
  tooltipContent: React.ReactNode;
  placement?: IFloatingTooltipProps['placement'];
} & Omit<IFloatingTooltipProps, 'arrowClassName' | 'children'> &
  ComponentPropsWithoutRef<'div'>;

/**
 * Shows a short hint when the user hovers or focuses the wrapped element.
 *
 * ## When to use
 *
 * - Naming an icon-only control (e.g. what a toolbar button does)
 * - Explaining why a control is disabled
 * - Showing truncated or abbreviated content in full
 *
 * ## When to choose another component
 *
 * - **Guidance on a form field** — use `Field.HelperText`. It is always visible.
 * - **Essential information** — put it in visible text. Tooltips do not appear
 *   on touch devices.
 * - **Rich or interactive content** — use a `Modal`, `Dropdown`, or inline
 *   disclosure. Tooltip content cannot be clicked or focused.
 *
 * ## Content rules
 *
 * - Keep content to a short phrase or one short sentence.
 * - Do **not** end the content with a full stop.
 * - Plain text only — no buttons, links, or inputs.
 *
 * ✅ `tooltipContent="Download as PDF"`
 *
 * ❌ `tooltipContent="Download as PDF."`
 *
 * ## Composition rules
 *
 * `Tooltip` wraps its children in an `inline-block` div. When that wrapper
 * breaks your layout, compose `FloatingTooltip` + `TooltipCore` directly:
 *
 * ```tsx
 * <FloatingTooltip enabled showArrow>
 *   <YourAnchorElement />
 *   <TooltipCore>Hint text</TooltipCore>
 * </FloatingTooltip>
 * ```
 */
const Tooltip: FC<PropsWithChildren<TooltipProps>> = (props) => {
  const {
    placement,
    showArrow,
    enabled,
    tooltipContent,
    className,
    children,
    ...rest
  } = props;

  return (
    <FloatingTooltip
      placement={placement}
      showArrow={showArrow}
      enabled={enabled}
      arrowClassName="fill-gray-900 shadow-sm"
    >
      <div className={clstx('inline-block', className)} {...rest}>
        {children}
      </div>

      <TooltipCore>{tooltipContent}</TooltipCore>
    </FloatingTooltip>
  );
};

export default Tooltip;
